import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ip_back } from "../../config";
import { useBrand } from "../../context/BrandContext";
import { usePermissions } from "../../context/Permissioncontext";
import { initializeSocket, disconnectSocket } from "../../socket";
import { persistLiveTrainingSession } from "../../hooks/useLiveTrainingRedirect";
import { playerpresent } from "../../components/funtionsBD/Mainservice";
import { HOME_ACTIVE_ROLE_KEY } from "../../utils/homeRole";
import {
  demoJoinApiQuery,
  DEMO_SESSION_CODE_DEFAULT,
  isDemoWalkInUser,
  normalizeDemoSessionCode,
  readDemoJoinTargetFromLocation,
} from "../../utils/demoClub";
import { LegalFooter } from "../../components/legal/LegalFooter";
import { LanguageShortcutFab } from "../../components/LanguageShortcutFab";
import { requestLiveFullscreen } from "../../hooks/useLiveFullscreen";
import { primeEvalCameraPermission } from "../../utils/evalCameraPermission";
import "./DemoJoinPage.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function persistDemoAuth(user, token) {
  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(user));
  try {
    localStorage.setItem(HOME_ACTIVE_ROLE_KEY, "player");
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event("smarttt:auth-changed"));
  } catch {
    /* ignore */
  }
}

function persistSessionFromJoin(session, group) {
  if (!session?.ts_id) return false;
  persistLiveTrainingSession(session, {
    gr_id: group?.gr_id || session.gr_id,
    gr_name: group?.gr_name || session.gr_name,
    groupDetails: {
      gr_id: group?.gr_id || session.gr_id,
      gr_name: group?.gr_name || session.gr_name,
      club_id: group?.club_id || null,
    },
  });
  return true;
}

export default function DemoJoinPage() {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { loadPermissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const joinTarget = useMemo(
    () => readDemoJoinTargetFromLocation(`?${searchParams.toString()}`),
    [searchParams]
  );

  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [sessionCode, setSessionCode] = useState(
    () => joinTarget.session_code || DEMO_SESSION_CODE_DEFAULT
  );
  const [pseudo, setPseudo] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [waitingForSession, setWaitingForSession] = useState(() => {
    try {
      return searchParams.get("wait") === "1";
    } catch {
      return false;
    }
  });

  const existingWalkIn = useMemo(() => {
    const user = readStoredUser();
    if (!isDemoWalkInUser(user) || !localStorage.getItem("token")) return null;
    return user;
  }, []);

  // Returning from a finished demo: keep waiting for the coach's next session.
  useEffect(() => {
    if (searchParams.get("wait") !== "1") return;
    if (!existingWalkIn) return;
    setWaitingForSession(true);
  }, [searchParams, existingWalkIn]);

  const loadInfo = useCallback(async () => {
    try {
      const res = await fetch(
        `${ip_back}api/demo/join${demoJoinApiQuery({
          session_code: sessionCode,
        })}`
      );
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.status === "error") {
        throw new Error(body?.message || "Demo is not available");
      }
      setInfo(body?.data || null);
      setInfoError("");
      return body?.data || null;
    } catch (err) {
      setInfoError(
        err?.message ||
          t("demoJoinUnavailable", "Demo is not available right now.")
      );
      return null;
    } finally {
      setLoadingInfo(false);
    }
  }, [t, sessionCode]);

  useEffect(() => {
    document.title = t("demoJoinTitle", "Join demo training") + " · SmartTT";
    loadInfo();
  }, [loadInfo, t]);

  const enterLiveTraining = useCallback(
    async (session, group, user, { primeCamera = false } = {}) => {
      if (primeCamera) {
        await primeEvalCameraPermission();
      }
      persistSessionFromJoin(session, group);
      if (session?.ts_id && user?.player_id) {
        try {
          await playerpresent({
            ts_id: session.ts_id,
            gr_id: group?.gr_id || session.gr_id,
            ts_present: true,
            player_id: user.player_id,
          });
        } catch {
          /* still enter live training */
        }
      }
      requestLiveFullscreen();
      loadPermissions(user?.permissions || []);
      navigate("/live-training", { replace: true });
    },
    [loadPermissions, navigate]
  );

  useEffect(() => {
    if (!waitingForSession) return undefined;
    let cancelled = false;
    const tick = async () => {
      const data = await loadInfo();
      if (cancelled || !data?.session?.ts_id) return;
      const user = readStoredUser();
      if (!user?.user_id) return;
      setWaitingForSession(false);
      enterLiveTraining(data.session, data.group, user);
    };
    const id = window.setInterval(tick, 4000);
    tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [waitingForSession, loadInfo, enterLiveTraining]);

  const handleContinueExisting = async () => {
    const user = readStoredUser();
    if (!user?.user_id) return;
    disconnectSocket();
    initializeSocket(localStorage.getItem("token"));
    if (info?.session?.ts_id) {
      await enterLiveTraining(info.session, info.group, user, { primeCamera: true });
      return;
    }
    setWaitingForSession(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const nick = String(pseudo || "").trim();
    if (!nick) {
      setError(t("pseudoRequired", "A player pseudo is required"));
      return;
    }
    const emailTrim = String(email || "").trim();
    if (emailTrim && !EMAIL_RE.test(emailTrim)) {
      setError(
        t("demoJoinEmailInvalid", "Enter a valid email, or leave it empty")
      );
      return;
    }
    if (!consent) {
      setError(
        t("demoJoinConsentRequired", "Tick the recording consent box to join")
      );
      return;
    }

    setSubmitting(true);
    try {
      const code =
        normalizeDemoSessionCode(sessionCode) || DEMO_SESSION_CODE_DEFAULT;
      const res = await fetch(`${ip_back}api/demo/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pseudo: nick,
          email: emailTrim || undefined,
          consent: true,
          session_code: code,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.status === "error") {
        throw new Error(body?.message || t("demoJoinFailed", "Could not join"));
      }
      const data = body.data || {};
      const token = data.token || data.user?.user_token;
      const user = data.user;
      if (!token || !user?.user_id) {
        throw new Error(t("demoJoinFailed", "Could not join"));
      }
      persistDemoAuth(user, token);
      disconnectSocket();
      initializeSocket(token);
      if (data.session?.ts_id) {
        await primeEvalCameraPermission();
        await enterLiveTraining(data.session, data.group, user);
        return;
      }
      throw new Error(
        t(
          "demoJoinNoSession",
          "Could not open live training. Ask the coach to start the demo, then try again."
        )
      );
    } catch (err) {
      setError(err?.message || t("demoJoinFailed", "Could not join"));
    } finally {
      setSubmitting(false);
    }
  };

  const clubName =
    info?.club_name || brand?.brand_name || t("demoJoinClubFallback", "Demo");
  const accent = brand?.color_primary || "#2ee6a6";

  return (
    <div className="demo-join" data-testid="app-shell" style={{ "--demo-accent": accent }}>
      <div className="demo-join__glow" aria-hidden />
      <header className="demo-join__top">
        <LanguageShortcutFab variant="invite" />
      </header>
      <main className="demo-join__main">
        <p className="demo-join__kicker">
          {t("demoJoinKicker", "Live training demo")}
        </p>
        <h1 className="demo-join__title">
          {t("demoJoinTitle", "Join with your phone")}
        </h1>
        <p className="demo-join__lead">
          {t("demoJoinLead", {
            club: clubName,
            defaultValue:
              "Enter a nickname, agree to recording, and join {{club}} live training. Email is optional. You do not need to be a club member.",
          })}
        </p>

        {loadingInfo ? (
          <p className="demo-join__status">
            {t("loading", "Loading…")}
          </p>
        ) : null}
        {infoError ? (
          <p className="demo-join__error" role="alert">
            {infoError}
          </p>
        ) : null}
        {info && !info.session_open && !waitingForSession ? (
          <p className="demo-join__hint">
            {t(
              "demoJoinWaitingCoach",
              "Register below. You will enter the demo hall as soon as live training is ready."
            )}
          </p>
        ) : null}

        {existingWalkIn && !waitingForSession ? (
          <button
            type="button"
            className="demo-join__continue"
            onClick={handleContinueExisting}
          >
            {t("demoJoinContinueAs", {
              name: existingWalkIn.user_alias || existingWalkIn.user_first_name,
              defaultValue: "Continue as {{name}}",
            })}
          </button>
        ) : null}

        {waitingForSession ? (
          <div className="demo-join__wait" role="status">
            <span className="demo-join__pulse" aria-hidden />
            <p>
              {searchParams.get("wait") === "1"
                ? t(
                    "demoJoinWaitingNextDemo",
                    "Waiting for access to the next demo. You will join automatically when the coach starts live training…"
                  )
                : t(
                    "demoJoinWaitingSession",
                    "Nickname registered. Waiting for the coach to start live training…"
                  )}
            </p>
          </div>
        ) : (
          <form className="demo-join__form" onSubmit={handleSubmit}>
            <label className="demo-join__field">
              <span>{t("demoJoinSessionCode", "Session code")}</span>
              <input
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value)}
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder={DEMO_SESSION_CODE_DEFAULT}
                disabled={submitting}
                required
              />
            </label>
            <label className="demo-join__field">
              <span>{t("playerPseudo", "Pseudo")}</span>
              <input
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value)}
                autoComplete="nickname"
                autoCapitalize="words"
                enterKeyHint="next"
                maxLength={80}
                placeholder={t("demoJoinPseudoPh", "Your nickname")}
                disabled={submitting}
                required
              />
            </label>
            <label className="demo-join__field">
              <span>{t("emailOptional", "Email (optional)")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                placeholder={t("demoJoinEmailPh", "Optional")}
                disabled={submitting}
              />
            </label>
            <label className={`demo-join__consent${consent ? " is-on" : ""}`}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={submitting}
              />
              <span>
                {t(
                  "demoJoinConsent",
                  "I agree to be video-recorded during this demo. Recordings and heart rate are kept for one month, then deleted. I am not joining as a club member."
                )}
              </span>
            </label>
            {error ? (
              <p className="demo-join__error" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              className="demo-join__submit"
              disabled={submitting}
            >
              {submitting
                ? t("saving", "Saving…")
                : t("demoJoinSubmit", "Join live training")}
            </button>
          </form>
        )}
      </main>
      <footer className="demo-join__footer">
        <p className="demo-join__legal-note">
          {t("demoJoinRetention", {
            days: info?.retention_days || 30,
            defaultValue:
              "Demo recordings are kept {{days}} days. You can read the privacy notice below.",
          })}{" "}
          <Link to="/privacy">{t("privacyPolicy", "Privacy")}</Link>
        </p>
        <LegalFooter />
      </footer>
    </div>
  );
}
